import React from "react";
import { osc, orbitAngle, wobble, stagger } from "../../core/AnimationUtils";

/* ═══════════════════════════════════════════════════════════════
   Chemistry & Molecules — 8 ANIMATED templates
   periodic_table_view, molecule_3d, chemical_bond, ph_scale,
   crystal_lattice, gas_particles, distillation_setup,
   electrochemistry_cell
   ═══════════════════════════════════════════════════════════════ */

/* ─── 1. Periodic Table View ──────────────────────────────── */
export const PeriodicTableView: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Periodic Table";
  const activeIndex = (props.activeIndex as number) ?? -1;
  /** Seconds since scene started — drives active-element pulse and tile shimmer. */
  const t = (props.t as number) ?? 0;

  const elements = ["H", "He", "Li", "Be", "B", "C", "N", "O", "F", "Ne", "Na", "Mg", "Al", "Si", "P", "S", "Cl", "Ar"];
  const colors = ["#e74c3c", "#9b59b6", "#3498db", "#2ecc71", "#f39c12", "#1abc9c"];

  // Active element: stroke width pulses
  const activeStroke = osc(t, 1.0, 3, 5);

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {elements.map((el, i) => {
        const col = i % 9, row = Math.floor(i / 9);
        const x = 80 + col * 72, y = 100 + row * 100;
        const c = colors[i % colors.length];
        // Each tile gets a subtle breathing opacity, staggered by index
        const tileOpacity = i === activeIndex ? 1 : osc(t, 4.0, 0.42, 0.58, stagger(i, 0.12));
        return (
          <g key={i}>
            <rect x={x} y={y} width="64" height="80" rx="8"
              fill={i === activeIndex ? c : "#1c2833"}
              stroke={i === activeIndex ? "#fff" : c}
              strokeWidth={i === activeIndex ? activeStroke : 1.5}
              opacity={tileOpacity} />
            <text x={x + 10} y={y + 18} fill="#7f8c8d" fontSize="10">{i + 1}</text>
            <text x={x + 32} y={y + 52} textAnchor="middle" fill="#ecf0f1" fontSize="22" fontWeight="700">{el}</text>
          </g>
        );
      })}
      {/* Legend */}
      {[
        { c: "#e74c3c", t: "Nonmetal" }, { c: "#3498db", t: "Metal" },
        { c: "#f39c12", t: "Metalloid" }, { c: "#9b59b6", t: "Noble Gas" },
      ].map((l, i) => (
        <g key={i}>
          <rect x={100 + i * 160} y="350" width="12" height="12" rx="2" fill={l.c} opacity="0.6" />
          <text x={118 + i * 160} y="361" fill="#bdc3c7" fontSize="11">{l.t}</text>
        </g>
      ))}
      <text x="400" y="440" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 2. Molecule 3D ──────────────────────────────────────── */
export const Molecule3d: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Water Molecule (H₂O)";
  /** Seconds since scene started — drives continuous rotation and atom breathing. */
  const t = (props.t as number) ?? 0;

  // Molecule rotates one full cycle every 6 seconds
  const rad = orbitAngle(t, 6.0);

  // Oxygen atom: gentle breathing radius
  const oxyR = osc(t, 2.0, 48, 53);

  // Hydrogen atoms: subtle size pulse, staggered
  const hRad0 = osc(t, 1.8, 33, 37, 0);
  const hRad1 = osc(t, 1.8, 33, 37, Math.PI);

  // Electron cloud: rx/ry oscillate slightly
  const cloudRx = osc(t, 3.0, 175, 188);
  const cloudRy = osc(t, 2.5, 115, 125, 0.8);

  // H positions
  const hx0 = 400 + Math.cos(rad + 0.9) * 130;
  const hy0 = 270 + Math.sin(rad + 0.9) * 80;
  const hx1 = 400 + Math.cos(rad - 0.9) * 130;
  const hy1 = 270 + Math.sin(rad - 0.9) * 80;

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Bond 0 */}
      <line x1="400" y1="270" x2={hx0} y2={hy0} stroke="#546e7a" strokeWidth="8" strokeLinecap="round" />
      {/* Bond 1 */}
      <line x1="400" y1="270" x2={hx1} y2={hy1} stroke="#546e7a" strokeWidth="8" strokeLinecap="round" />
      {/* Hydrogen 0 */}
      <circle cx={hx0} cy={hy0} r={hRad0} fill="#3498db" opacity="0.8" />
      <text x={hx0} y={hy0 + 7} textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="700">H</text>
      {/* Hydrogen 1 */}
      <circle cx={hx1} cy={hy1} r={hRad1} fill="#3498db" opacity="0.8" />
      <text x={hx1} y={hy1 + 7} textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="700">H</text>
      {/* Oxygen */}
      <circle cx="400" cy="270" r={oxyR} fill="#e74c3c" opacity="0.8" />
      <text x="400" y="278" textAnchor="middle" fill="#ecf0f1" fontSize="24" fontWeight="700">O</text>
      {/* Electron cloud hint — rx/ry breathe */}
      <ellipse cx="400" cy="270" rx={cloudRx} ry={cloudRy}
        fill="none" stroke="#42a5f5" strokeWidth="1" strokeDasharray="6,6" opacity="0.18" />
      {/* Bond angle label */}
      <text x="400" y="180" textAnchor="middle" fill="#bdc3c7" fontSize="14">104.5° bond angle</text>
      <text x="400" y="480" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 3. Chemical Bond ────────────────────────────────────── */
export const ChemicalBond: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Chemical Bonds";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const types = (props.types as string[]) || ["Ionic", "Covalent", "Metallic"];
  /** Seconds since scene started — drives electron transfer, shared e- oscillation, sea drift. */
  const t = (props.t as number) ?? 0;

  // Ionic bond: electron particle slides from Na to Cl side over 1.5 s
  const ionicElX = osc(t, 1.5, 150 - 20, 150 + 20);  // 130 → 170

  // Covalent bond: two shared electrons oscillate back and forth
  const covEl0X = osc(t, 1.0, 400 - 8, 400 + 8);
  const covEl1X = osc(t, 1.0, 400 + 8, 400 - 8);  // opposite phase

  // Metallic bond: 5 free electrons drift individually (index-seeded period)
  const metElPositions = [0, 1, 2, 3, 4].map((j) => ({
    cx: osc(t, 1.5 + j * 0.15, 650 - 30 + j * 15, 650 - 30 + ((j + 2) % 5) * 15, j * 0.6),
    cy: 215 + (j % 2) * 15,
  }));

  // Active atom radius pulse
  const activeR = osc(t, 1.2, 35, 39);

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {[
        { x: 150, atoms: [{ el: "Na⁺", c: "#3498db" }, { el: "Cl⁻", c: "#2ecc71" }] },
        { x: 400, atoms: [{ el: "O",   c: "#e74c3c" }, { el: "O",   c: "#e74c3c" }] },
        { x: 650, atoms: [{ el: "Fe",  c: "#bdbdbd" }, { el: "Fe",  c: "#bdbdbd" }] },
      ].map((bond, i) => (
        <g key={i}>
          <text x={bond.x} y="100" textAnchor="middle"
            fill={i === activeIndex ? "#f9a825" : "#bdc3c7"}
            fontSize="16" fontWeight="600">{types[i]}</text>
          {/* Atom 1 */}
          <circle cx={bond.x - 45} cy="220"
            r={i === activeIndex ? activeR : 30}
            fill={bond.atoms[0].c}
            opacity={i === activeIndex ? 0.9 : 0.5} />
          <text x={bond.x - 45} y="226" textAnchor="middle" fill="#ecf0f1" fontSize="14">{bond.atoms[0].el}</text>

          {/* Bond visualisation */}
          {i === 0 && (
            <g>
              <text x={bond.x} y="226" textAnchor="middle" fill="#f9a825" fontSize="20">⟷</text>
              {/* Ionic: electron particle slides — pure t */}
              <circle cx={ionicElX} cy="215" r="4" fill="#f9a825" />
            </g>
          )}
          {i === 1 && (
            <g>
              <line x1={bond.x - 15} y1="215" x2={bond.x + 15} y2="215" stroke="#ecf0f1" strokeWidth="3" />
              <line x1={bond.x - 15} y1="225" x2={bond.x + 15} y2="225" stroke="#ecf0f1" strokeWidth="3" />
              {/* Shared electrons oscillate — pure t */}
              <circle cx={covEl0X} cy="210" r="3" fill="#f9a825" />
              <circle cx={covEl1X} cy="230" r="3" fill="#f9a825" />
            </g>
          )}
          {i === 2 && (
            <g>
              {/* Sea of electrons drifting — pure t */}
              {metElPositions.map((pos, j) => (
                <circle key={j} cx={pos.cx} cy={pos.cy} r="3" fill="#f9a825" opacity="0.65" />
              ))}
            </g>
          )}

          {/* Atom 2 */}
          <circle cx={bond.x + 45} cy="220"
            r={i === activeIndex ? activeR : 30}
            fill={bond.atoms[1].c}
            opacity={i === activeIndex ? 0.9 : 0.5} />
          <text x={bond.x + 45} y="226" textAnchor="middle" fill="#ecf0f1" fontSize="14">{bond.atoms[1].el}</text>
        </g>
      ))}
      <text x="400" y="400" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 4. pH Scale ─────────────────────────────────────────── */
export const PhScale: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "pH Scale";
  const activeIndex = (props.activeIndex as number) ?? -1;
  /** Seconds since scene started — drives active bar height pulse and indicator bob. */
  const t = (props.t as number) ?? 0;

  // Active bar: height breathes between 180 and 195
  const activeBarH = osc(t, 1.0, 178, 195);
  // Active bar: y rises slightly to match height increase (keeps bottom anchored)
  const activeBarY = 200 - (activeBarH - 180);

  // A small "pH meter" indicator bob — floats up-down above the active bar
  const indicatorBob = wobble(t, 2.0, 4);

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Scale bar */}
      {Array.from({ length: 15 }, (_, i) => {
        const hue = (i / 14) * 240;
        const x = 80 + i * 44;
        const isActive = i === activeIndex;
        const barH = isActive ? activeBarH : 180;
        const barY = isActive ? activeBarY : 200;
        return (
          <g key={i}>
            <rect x={x} y={barY} width="40" height={barH} rx="4"
              fill={`hsl(${hue}, 70%, ${isActive ? 55 : 40}%)`}
              opacity={isActive ? 1 : 0.6}
              stroke={isActive ? "#fff" : "none"}
              strokeWidth={isActive ? 2 : 0} />
            <text x={x + 20} y="405" textAnchor="middle" fill="#bdc3c7" fontSize="12" fontWeight="600">{i}</text>
          </g>
        );
      })}
      {/* Floating indicator above active bar */}
      {activeIndex >= 0 && activeIndex < 15 && (
        <circle
          cx={80 + activeIndex * 44 + 20}
          cy={activeBarY - 12 + indicatorBob}
          r="6" fill="#fff" opacity="0.85" />
      )}
      {/* Labels */}
      <text x="160" y="170" textAnchor="middle" fill="#e74c3c" fontSize="16" fontWeight="700">Acidic</text>
      <text x="400" y="170" textAnchor="middle" fill="#4caf50" fontSize="16" fontWeight="700">Neutral</text>
      <text x="640" y="170" textAnchor="middle" fill="#3498db" fontSize="16" fontWeight="700">Basic</text>
      {/* Examples */}
      <text x="120" y="450" textAnchor="middle" fill="#7f8c8d" fontSize="10">Battery</text>
      <text x="210" y="450" textAnchor="middle" fill="#7f8c8d" fontSize="10">Lemon</text>
      <text x="400" y="450" textAnchor="middle" fill="#7f8c8d" fontSize="10">Water</text>
      <text x="560" y="450" textAnchor="middle" fill="#7f8c8d" fontSize="10">Soap</text>
      <text x="660" y="450" textAnchor="middle" fill="#7f8c8d" fontSize="10">Bleach</text>
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 5. Crystal Lattice ──────────────────────────────────── */
export const CrystalLattice: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Crystal Lattice";
  /** Seconds since scene started — drives thermal vibration and slow perspective rotation. */
  const t = (props.t as number) ?? 0;

  // Slow perspective rotation: one cycle every 8 s — shifts the 3-D projection offset
  const rotAngle = orbitAngle(t, 8.0);
  const offset = Math.sin(rotAngle) * 8;

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* 3D lattice grid */}
      {Array.from({ length: 4 }, (_, r) =>
        Array.from({ length: 4 }, (_, c) => {
          const bx = 200 + c * 100 + r * 30 + offset;
          const by = 120 + r * 100 - c * 15 + offset * 0.5;
          // Each atom vibrates (thermal motion): small wobble seeded by (r,c)
          const vx = wobble(t, 1.2 + (r + c) * 0.07, 2.0, (r * 4 + c) * 0.9);
          const vy = wobble(t, 1.4 + (r + c) * 0.05, 2.0, (r * 4 + c) * 1.3 + 1.0);
          const ax = bx + vx, ay = by + vy;
          // Radius pulses: slightly warmer atoms in centre
          const atomR = osc(t, 2.0 + (r + c) * 0.2, 13, 15.5, (r * 4 + c) * 0.4);
          return (
            <g key={`${r}-${c}`}>
              {/* Bonds */}
              {c < 3 && (
                <line x1={ax} y1={ay}
                  x2={200 + (c + 1) * 100 + r * 30 + offset + wobble(t, 1.2 + (r + c + 1) * 0.07, 2.0, (r * 4 + c + 1) * 0.9)}
                  y2={120 + r * 100 - (c + 1) * 15 + offset * 0.5 + wobble(t, 1.4 + (r + c + 1) * 0.05, 2.0, (r * 4 + c + 1) * 1.3 + 1.0)}
                  stroke="#546e7a" strokeWidth="1.5" opacity="0.4" />
              )}
              {r < 3 && (
                <line x1={ax} y1={ay}
                  x2={200 + c * 100 + (r + 1) * 30 + offset + wobble(t, 1.2 + (r + 1 + c) * 0.07, 2.0, ((r + 1) * 4 + c) * 0.9)}
                  y2={120 + (r + 1) * 100 - c * 15 + offset * 0.5 + wobble(t, 1.4 + (r + 1 + c) * 0.05, 2.0, ((r + 1) * 4 + c) * 1.3 + 1.0)}
                  stroke="#546e7a" strokeWidth="1.5" opacity="0.4" />
              )}
              {/* Atom */}
              <circle cx={ax} cy={ay} r={atomR}
                fill={(r + c) % 2 === 0 ? "#3498db" : "#e74c3c"} opacity="0.75" />
            </g>
          );
        })
      )}
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 6. Gas Particles ────────────────────────────────────── */
export const GasParticles: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Gas Behavior";
  /** Seconds since scene started — drives particle motion and temperature indicator. */
  const t = (props.t as number) ?? 0;

  const particleCount = 20;
  const colors = ["#e74c3c", "#3498db", "#2ecc71", "#f9a825", "#9b59b6"];

  // Container bounds
  const cxMin = 155, cxMax = 645, cyMin = 85, cyMax = 475;
  const cxRange = cxMax - cxMin, cyRange = cyMax - cyMin;

  // Temperature oscillates slowly — particles speed up when hotter
  const tempFactor = osc(t, 6.0, 0.6, 1.4);  // modulates speed
  const tempBarH = osc(t, 6.0, 40, 280);

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Container */}
      <rect x="150" y="80" width="500" height="400" rx="12" fill="none" stroke="#546e7a" strokeWidth="3" />
      {/* Particles — position from t, pseudo-random from index */}
      {Array.from({ length: particleCount }, (_, i) => {
        // Each particle has a unique "base" position and velocity direction
        // derived from its index — no Math.random()
        const hash = i * 137.508;
        // Base centre of motion (deterministic from i)
        const baseCx = cxMin + 20 + (hash % (cxRange - 40));
        const baseCy = cyMin + 20 + ((hash * 1.618) % (cyRange - 40));
        // Speed: scaled by tempFactor so particles move faster when hotter
        const speedX = (0.7 + (i % 5) * 0.15) * tempFactor;
        const speedY = (0.6 + (i % 4) * 0.18) * tempFactor;
        // Phase offset unique per particle
        const phaseX = i * 0.97;
        const phaseY = i * 1.31;
        // Position oscillates inside container, bouncing via sin
        const px = baseCx + Math.sin(t * speedX + phaseX) * 35;
        const py = baseCy + Math.cos(t * speedY + phaseY) * 35;
        // Clamp to container interior
        const clampedX = Math.max(cxMin + 8, Math.min(cxMax - 8, px));
        const clampedY = Math.max(cyMin + 8, Math.min(cyMax - 8, py));
        return (
          <circle key={i} cx={clampedX} cy={clampedY}
            r={5 + (i % 3)}
            fill={colors[i % colors.length]}
            opacity="0.75" />
        );
      })}
      {/* Temperature indicator */}
      <rect x="700" y="120" width="30" height="320" rx="8" fill="#1c2833" stroke="#546e7a" strokeWidth="1" />
      <rect x="704" y={440 - tempBarH} width="22" height={tempBarH} rx="6" fill="#e74c3c"
        opacity={osc(t, 1.0, 0.55, 0.85)} />
      <text x="715" y="460" textAnchor="middle" fill="#7f8c8d" fontSize="10">Temp</text>
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 7. Distillation Setup ───────────────────────────────── */
export const DistillationSetup: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Distillation";
  const flowProgress = (props.flowProgress as number) || 0;
  /** Seconds since scene started — drives flame flicker, bubble rise, vapor flow. */
  const t = (props.t as number) ?? 0;

  // Flame: tip height oscillates (shorter = hotter snap, longer = cooler curl)
  const flameTipY = osc(t, 0.5, 373, 383);  // y of flame tip (lower = taller)
  const flameSwayL = wobble(t, 0.5, 6, 0.0);
  const flameSwayR = wobble(t, 0.5, 6, Math.PI);

  // Bubbles: 3 rising, staggered
  const bubbles = [0, 1, 2].map((i) => {
    const phase = stagger(i, 0.33);
    const pos = ((t / (1.0 + i * 0.3)) + phase) % 1;  // 0→1
    return { cx: 210 + i * 20, cy: 370 - pos * 70, opacity: 0.3 * (1 - pos), r: 3 + i };
  });

  // Vapor/condensate particle along condenser path
  // Path: (260,180) → (400,120) → (550,180) → (580,300) → (580,380)
  // Normalised 0→1 over 3 s
  const vPos = (t / 3.0) % 1;
  let vx: number, vy: number;
  if (vPos < 0.25) {
    const p = vPos / 0.25;
    vx = 260 + p * (400 - 260); vy = 180 + p * (120 - 180);
  } else if (vPos < 0.5) {
    const p = (vPos - 0.25) / 0.25;
    vx = 400 + p * (550 - 400); vy = 120 + p * (180 - 120);
  } else if (vPos < 0.75) {
    const p = (vPos - 0.5) / 0.25;
    vx = 550 + p * (580 - 550); vy = 180 + p * (300 - 180);
  } else {
    const p = (vPos - 0.75) / 0.25;
    vx = 580; vy = 300 + p * (380 - 300);
  }

  // Collected liquid level rises slowly (based on flowProgress + t oscillation)
  const liquidH = flowProgress * 80 + osc(t, 3.0, -3, 3);

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Boiling flask */}
      <path d="M180,400 Q180,300 200,250 L200,150 L260,150 L260,250 Q280,300 280,400 Q230,420 180,400"
        fill="none" stroke="#42a5f5" strokeWidth="2" />
      <rect x="180" y="350" width="100" height="50" fill="#42a5f5" opacity="0.2" />
      {/* Bubbles — pure t */}
      {bubbles.map((b, i) => (
        <circle key={i} cx={b.cx} cy={b.cy} r={b.r} fill="#42a5f5" opacity={b.opacity} />
      ))}
      {/* Flame — shape from t */}
      <path d={`M230,420 Q${220 + flameSwayL},405 230,${flameTipY} Q${240 + flameSwayR},405 230,420`}
        fill="#ff9800" opacity="0.75" />
      {/* Condenser tube */}
      <path d="M260,180 L400,120 L550,180" fill="none" stroke="#bdbdbd" strokeWidth="3" />
      {/* Cooling water jacket */}
      <rect x="350" y="110" width="120" height="30" rx="6" fill="none" stroke="#42a5f5" strokeWidth="1" strokeDasharray="4,3" />
      <text x="410" y="105" textAnchor="middle" fill="#42a5f5" fontSize="10">Cooling Water</text>
      {/* Vapor/liquid particle travelling condenser path — pure t */}
      <circle cx={vx} cy={vy} r="4" fill="#90caf9" opacity="0.7" />
      {/* Collection flask */}
      <path d="M550,400 Q550,300 570,250 L570,180 L600,180 L600,250 Q620,300 620,400 Q585,420 550,400"
        fill="none" stroke="#42a5f5" strokeWidth="2" />
      {/* Collected liquid */}
      <rect x="555" y={400 - Math.max(0, liquidH)} width="60" height={Math.max(0, liquidH)}
        fill="#42a5f5" opacity="0.18" rx="0" />
      {/* Labels */}
      <text x="230" y="460" textAnchor="middle" fill="#7f8c8d" fontSize="11">Boiling Flask</text>
      <text x="585" y="430" textAnchor="middle" fill="#7f8c8d" fontSize="11">Collection</text>
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 8. Electrochemistry Cell ────────────────────────────── */
export const ElectrochemistryCell: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Electrochemistry";
  const flowProgress = (props.flowProgress as number) || 0;
  /** Seconds since scene started — drives electron flow, ion drift, bulb glow. */
  const t = (props.t as number) ?? 0;

  // Electrons: 3 particles travelling the wire path anode→cathode (external circuit)
  // Wire path: (265,220) → (265,120) → (535,120) → (535,220)
  // Total approximate length: 100 + 270 + 100 = 470 px; normalise to 0→1 in 2 s
  const electrons = [0, 1, 2].map((i) => {
    const phase = stagger(i, 1 / 3);
    const pos = ((t / 2.0) + phase) % 1;
    let ex: number, ey: number;
    if (pos < 100 / 470) {
      // Segment 1: up from anode
      const p = pos / (100 / 470);
      ex = 265; ey = 220 - p * 100;
    } else if (pos < 370 / 470) {
      // Segment 2: across top
      const p = (pos - 100 / 470) / (270 / 470);
      ex = 265 + p * 270; ey = 120;
    } else {
      // Segment 3: down to cathode
      const p = (pos - 370 / 470) / (100 / 470);
      ex = 535; ey = 120 + p * 100;
    }
    return { ex, ey };
  });

  // Ions: 6 red (anode side) and 6 blue (cathode side), drifting in solution
  const anodeIons = Array.from({ length: 6 }, (_, i) => ({
    cx: osc(t, 1.5 + i * 0.2, 240 + (i % 3) * 40, 260 + (i % 3) * 40, i * 0.7),
    cy: 300 + Math.floor(i / 3) * 40,
  }));
  const cathodeIons = Array.from({ length: 6 }, (_, i) => ({
    cx: 480 + (i % 3) * 30,
    cy: osc(t, 1.8 + i * 0.2, 280 + Math.floor(i / 3) * 50, 295 + Math.floor(i / 3) * 50, i * 0.9),
  }));

  // Bulb glow: brightness pulses when flowProgress is sufficient
  const bulbBrightness = flowProgress > 0.3 ? osc(t, 1.0, 0.7, 1.0) : 0;
  const bulbFill = bulbBrightness > 0 ? `rgba(255,249,196,${bulbBrightness})` : "#2c3e50";

  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Beaker */}
      <rect x="200" y="180" width="400" height="280" rx="8" fill="none" stroke="#546e7a" strokeWidth="3" />
      {/* Electrolyte */}
      <rect x="204" y="250" width="392" height="206" fill="#42a5f5" opacity="0.1" />
      {/* Salt bridge */}
      <rect x="380" y="200" width="40" height="200" rx="8" fill="#bdbdbd" opacity="0.2" stroke="#bdbdbd" strokeWidth="1" />
      <text x="400" y="310" textAnchor="middle" fill="#7f8c8d" fontSize="9" transform="rotate(-90,400,310)">Salt Bridge</text>
      {/* Anode */}
      <rect x="250" y="220" width="30" height="200" rx="4" fill="#546e7a" stroke="#455a64" strokeWidth="2" />
      <text x="265" y="195" textAnchor="middle" fill="#e74c3c" fontSize="14" fontWeight="700">−</text>
      <text x="265" y="440" textAnchor="middle" fill="#7f8c8d" fontSize="11">Anode</text>
      {/* Cathode */}
      <rect x="520" y="220" width="30" height="200" rx="4" fill="#8d6e63" stroke="#6d4c41" strokeWidth="2" />
      <text x="535" y="195" textAnchor="middle" fill="#3498db" fontSize="14" fontWeight="700">+</text>
      <text x="535" y="440" textAnchor="middle" fill="#7f8c8d" fontSize="11">Cathode</text>
      {/* Wire */}
      <path d="M265,220 L265,120 L535,120 L535,220" fill="none" stroke="#bdbdbd" strokeWidth="2" />
      {/* Electrons flowing — pure t */}
      {electrons.map((e, i) => (
        <circle key={i} cx={e.ex} cy={e.ey} r="4" fill="#f9a825" opacity="0.9" />
      ))}
      {/* Anode ions (red) drifting — pure t */}
      {anodeIons.map((ion, i) => (
        <circle key={i} cx={ion.cx} cy={ion.cy} r="5" fill="#e74c3c" opacity="0.45" />
      ))}
      {/* Cathode ions (blue) drifting — pure t */}
      {cathodeIons.map((ion, i) => (
        <circle key={i} cx={ion.cx} cy={ion.cy} r="5" fill="#3498db" opacity="0.45" />
      ))}
      {/* Light bulb — glow from t */}
      <circle cx="400" cy="100" r="15" fill={bulbFill} stroke="#bdbdbd" strokeWidth="2" />
      <text x="400" y="80" textAnchor="middle" fill="#7f8c8d" fontSize="10">💡</text>
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};
